import { useEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAppContext, useSelectedBroker } from '../contexts/hooks';
import { useAuthBootstrap } from '../hooks/useAuthBootstrap';
import { useAccountData } from '../hooks/useAccountData';
import AccountCard from '../components/AccountCard';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { trackEvent } from '../lib/analytics';
import { Card, CardContent } from '@/components/ui/card';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

export default function AccountOverview() {
  const { state, dispatch } = useAppContext();
  const broker = useSelectedBroker();
  const { bootstrap, status: authStatus, progress: authProgress } = useAuthBootstrap();
  const { loading, error, clearError, refetch } = useAccountData();
  const trackedOverviewKeyRef = useRef<string | null>(null);
  const brokerAuth = broker && state.authContext?.brokerId === broker.id ? state.authContext : null;
  const pageError = error ?? state.error;

  function retry() {
    clearError();
    dispatch({ type: 'SET_ERROR', error: null });
    if (!broker) return;
    if (!USE_MOCK && !brokerAuth) {
      bootstrap(broker.id).catch(() => {});
      return;
    }
    refetch();
  }

  useEffect(() => {
    trackEvent('component_viewed', {
      component: 'AccountOverview',
      page: 'account_overview',
      broker_id: broker?.id ?? undefined,
      chain: state.chain ?? undefined,
      status: broker?.accounts.length ?? 0,
    });
  }, [broker?.id, broker?.accounts.length, state.chain]);

  useEffect(() => {
    if (USE_MOCK || !broker || !state.walletAddress || brokerAuth) return;
    bootstrap(broker.id).catch(() => {});
  }, [broker, state.walletAddress, brokerAuth, bootstrap]);

  useEffect(() => {
    if (!broker) return;
    const key = `${broker.id}:${state.chain ?? 'unknown'}`;
    if (trackedOverviewKeyRef.current === key) return;

    trackEvent('account_list_viewed', {
      broker_id: broker.id,
      chain: state.chain ?? undefined,
      account_count: broker.accounts.length,
    });
    trackedOverviewKeyRef.current = key;
  }, [broker, state.chain]);

  if (!broker) return null;

  return (
    <div className="space-y-8">
      <div>
        <button
          type="button"
          className="inline-flex items-center gap-2 text-sm font-medium text-[var(--fg-secondary)] transition-colors hover:text-[var(--fg-primary)]"
          onClick={() => dispatch({ type: 'BACK_TO_BROKERS' })}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to DEX selection
        </button>
        <h2 className="mt-5 font-display text-2xl text-[var(--fg-base)]">{broker.name}</h2>
        <p className="mt-2 text-sm text-[var(--fg-secondary)]">Select an account to begin offboarding.</p>
      </div>

      {pageError && <ErrorMessage message={pageError} onRetry={retry} />}

      {authStatus === 'loading' ? (
        <LoadingSpinner message={authProgress || 'Checking API key...'} />
      ) : loading ? (
        <LoadingSpinner message="Loading account data..." />
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {broker.accounts.map((account) => (
            <AccountCard
              key={account.accountId}
              account={account}
              onClick={() => {
                trackEvent('account_selected', {
                  account_id: account.accountId,
                  account_type: account.type,
                  broker_id: broker.id,
                });
                dispatch({ type: 'SELECT_ACCOUNT', accountId: account.accountId });
              }}
            />
          ))}
          {broker.accounts.length === 0 && !loading && (
            <Card className="border-dashed border-[var(--border-subtle)] bg-[var(--bg-surface-primary)] py-0">
              <CardContent className="p-8 text-center text-sm text-[var(--fg-secondary)]">
                No accounts found.
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
